import { useCallback, useEffect, useState } from "react";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { RootState } from "../../../../../../app/rootReducer";
import { BookingInputs } from "../../../../Domain/Types/BookingInputs.type";
import { IFloorPayload } from "../../../../Domain/Types/FloorPlan/FloorPayload.type";
import { IPlaceSchedule } from "../../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../../Domain/Types/FloorPlan/ZoneSchedule";
import { useRemoteFetchAllCateringServices } from "../../../../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteFetchBookableEquipments } from "../../../../../../hooks/Remote/BookableEquipment/useRemoteFetchBookableEquipment";
import { useRemoteFetchRoomlayout } from "../../../../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import { useRemoteFetchPermissionContext } from "../../../../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";
import {
  BookingServiceBookableEquipData,
  BookingServiceEditedData,
  ServiceOrderDto
} from "./Domain/BookingServicePrompt.types";
import {
  handleAssignedBookableEquipItems,
  handleAssignedCateringItems,
  handleAssignedRoomLayItems
} from "./functions/handleAssignedServiceItems.function";
import { intiateCateringData } from "./Catering/BookingService-CateringTable.functions";
import { initiateBookableEquipmentData } from "./BookableEquipment/BookingService-BookableEquipment.functions";
import { initiateRoomLayoutData } from "./RoomLayout/BookingService-RoomLayoutTable.functions";
import { useSelector } from "../../../../../../app/helpers";
import { BookingServicePromptDialog } from "./BookingServicePromptDialog";
import { MaxSetUpTime } from "../../../../../../features/Booking-Form/typings/booking-services.types";

const emptyService = {
  cateringData: [],
  bookableEquipmentData: [],
  roomLayoutData: []
};
const emptyOrder = {
  caterings: [],
  costCenterId: null,
  bookableEquipments: [],
  roomLayout: [] // will be only one assigned
};

interface Props {
  userId: string;
  bookingInputs: BookingInputs;
  floorPlanForService: IFloorPayload | undefined;
  selectedEntry: IPlaceSchedule | IZoneSchedule | undefined;
  isServiceAdminBypass?: boolean;
  selectedZone?: number;
  remoteMaxSetupTime: MaxSetUpTime | undefined;
  isModalOpen: boolean;
  setIsModalOpen: (o: boolean) => void;
  isBookingServiceLoading: boolean;
  setIsBookingServiceLoading: (loading: boolean) => void;
}

/**
 * @version 0.0.1
 * a parent component that handles the relevant data and shows the service dialog
 */
export function BookingServicePrompt({
  userId,
  bookingInputs,
  floorPlanForService,
  selectedEntry,
  isServiceAdminBypass = false,
  selectedZone,
  remoteMaxSetupTime,
  isModalOpen,
  setIsModalOpen,
  isBookingServiceLoading,
  setIsBookingServiceLoading
}: Readonly<Props>) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const dialogStyle = {
    maxWidth: isMobile ? "inherit" : "70vw",
    width: isMobile ? "100%" : "70vw"
  };

  const { conferenceService } = useSelector((state: RootState) => state.booking.inputs);
  const selectedLocationId = bookingInputs.selectedLocation;

  const { data: remoteCateringMenu } = useRemoteFetchAllCateringServices(selectedLocationId);
  const { data: remoteBookableEquipment } = useRemoteFetchBookableEquipments();
  const { data: remoteRoomLayout } = useRemoteFetchRoomlayout();

  // to check permission of catering items
  const { data: permissionContextData } = useRemoteFetchPermissionContext(true);
  const permissionContext = permissionContextData?.[0];

  /** initial service available items and whole edited data of booking service table */
  const [editedService, setEditedService] = useState<BookingServiceEditedData>(emptyService);
  const [orderedService, setOrderedService] = useState<ServiceOrderDto>(emptyOrder);

  const createInitialDataInitState = () => ({
    catering: false,
    bookableEquipment: false,
    roomLayout: false,
    maxSetUpTime: false
  });
  const [dataInitState, setDataInitState] = useState(createInitialDataInitState);
  const markDataInitialized = useCallback(
    (key: keyof ReturnType<typeof createInitialDataInitState>) => {
      setDataInitState(prev => (prev[key] ? prev : { ...prev, [key]: true }));
    },
    []
  );

  const entryId = selectedEntry?.inventory?.id || selectedEntry?.inventoryId || 0;
  const checkNullableItems = (items: any[]) =>
    items.filter((item): item is NonNullable<typeof item> => item !== null && item !== undefined);

  const handleClose = () => {
    setIsModalOpen(false);
    /** deleted: setEditedService(emptyService); */
    setOrderedService(emptyOrder);
    setDataInitState(() => createInitialDataInitState());
  };

  // check max setup time data initially
  useEffect(() => {
    if (remoteMaxSetupTime?.maxSetUpTime !== undefined) markDataInitialized("maxSetUpTime");
  }, [remoteMaxSetupTime]);

  // find selectable catering items
  useEffect(() => {
    if (
      !selectedZone ||
      !floorPlanForService ||
      !remoteCateringMenu ||
      !permissionContext ||
      !remoteMaxSetupTime
    )
      return;

    const assignedCateringItems = handleAssignedCateringItems({
      floorPlanForService,
      selectedZone,
      remoteCateringMenu,
      permissionContext,
      userId
    });

    if (assignedCateringItems.length > 0) {
      intiateCateringData({
        bookingInputs,
        maxSetupTime: remoteMaxSetupTime.maxSetUpTime,
        assignedCateringItems: checkNullableItems(assignedCateringItems),
        conferenceService: conferenceService?.[entryId],
        isServiceAdminBypass,
        setEditedService,
        setOrderedService
      });
    }
    markDataInitialized("catering");
  }, [
    floorPlanForService,
    remoteCateringMenu,
    selectedZone,
    permissionContext,
    remoteMaxSetupTime,
    entryId,
    isModalOpen
  ]);

  // find selectable bookable equipments
  useEffect(() => {
    if (!selectedZone || !floorPlanForService || !remoteBookableEquipment || !remoteMaxSetupTime)
      return;

    const assignedBookableEquipItems = handleAssignedBookableEquipItems({
      floorPlanForService,
      selectedZone,
      remoteBookableEquipment
    });

    if (assignedBookableEquipItems.length > 0) {
      initiateBookableEquipmentData({
        bookingInputs,
        maxSetupTime: remoteMaxSetupTime.maxSetUpTime,
        assignedBookableEquipItems: assignedBookableEquipItems as BookingServiceBookableEquipData[],
        conferenceService: conferenceService?.[entryId],
        isServiceAdminBypass,
        setEditedService,
        setOrderedService
      });
    }
    markDataInitialized("bookableEquipment");
  }, [
    floorPlanForService,
    remoteBookableEquipment,
    selectedZone,
    remoteMaxSetupTime,
    entryId,
    isModalOpen
  ]);

  // find selectable room layout
  useEffect(() => {
    if (!selectedZone || !floorPlanForService || !remoteRoomLayout || !remoteMaxSetupTime) return;
    const assignedRoomLayoutItems = handleAssignedRoomLayItems({
      floorPlanForService,
      selectedZone,
      remoteRoomLayout: remoteRoomLayout.map(item => ({
        ...item,
        arrangement: item.arrangement
      }))
    });

    if (assignedRoomLayoutItems.length > 0) {
      initiateRoomLayoutData({
        bookingInputs,
        maxSetupTime: remoteMaxSetupTime.maxSetUpTime,
        assignedRoomLayoutItems: checkNullableItems(assignedRoomLayoutItems),
        conferenceService: conferenceService?.[entryId],
        isServiceAdminBypass,
        setEditedService,
        setOrderedService
      });
    }
    markDataInitialized("roomLayout");
  }, [
    floorPlanForService,
    remoteRoomLayout,
    selectedZone,
    remoteMaxSetupTime,
    entryId,
    isModalOpen
  ]);

  useEffect(() => {
    const allDataInitialized = Object.values(dataInitState).every(value => value === true);
    if (allDataInitialized) setIsBookingServiceLoading(false);
  }, [dataInitState]);

  return (
    <Dialog
      open={!isBookingServiceLoading && isModalOpen}
      onClose={handleClose}
      data-testid={"service-booking-detail-modal"}
      slotProps={{ paper: { sx: { ...dialogStyle } } }}
    >
      <BookingServicePromptDialog
        entryId={entryId}
        editedService={editedService}
        setEditedService={setEditedService}
        orderedService={orderedService}
        setOrderedService={setOrderedService}
        handleModalClose={handleClose}
      />
    </Dialog>
  );
}
